import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/16/solid';
import { CogIcon, FolderIcon, LinkIcon, MagnifyingGlassIcon as SearchIcon } from '@heroicons/react/24/outline';
import { Button } from '../../catalyst/button';
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownHeading,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
  DropdownSection,
} from '../../catalyst/dropdown';
import { useToast } from '../../components/Toast';

// Status questions are answered by the project's tables; the graph answers "what connects to what".
const STATUS_TABLES: { tab: string; label: string }[] = [
  { tab: 'ad', label: 'AD status' },
  { tab: 'sb', label: 'SB status' },
  { tab: 'llp', label: 'LLP status' },
  { tab: 'ldnd', label: 'LDND' },
  { tab: 'qec', label: 'QEC' },
  { tab: 'engine', label: 'Engine' },
  { tab: 'utilisation', label: 'Utilisation' },
];

type Props = {
  projectId: string;
  /** The working engine's node id, or null when the graph has none. */
  engineId: string | null;
  onFocus: (nodeId: string | null) => void;
  onFind: () => void;
};

/** The graph's entry points: re-focus the view, search it, copy a link to it, or leave for a status table. */
export function GraphTaskMenu({ projectId, engineId, onFocus, onFind }: Props) {
  const toast = useToast();
  // The URL holds the view (see useViewUrl), so the address is the link. A clipboard write can reject (the page
  // lost focus, say); the toast then says so.
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link to this view copied');
    } catch {
      toast.error('Could not copy the link');
    }
  };
  return (
    <Dropdown>
      <DropdownButton as={Button} outline>
        <MagnifyingGlassIcon className="size-4" />
        Find…
        <ChevronDownIcon />
      </DropdownButton>
      <DropdownMenu anchor="bottom end">
        <DropdownSection>
          <DropdownHeading>In this graph</DropdownHeading>
          <DropdownItem onClick={() => onFocus(null)}>
            <FolderIcon />
            <DropdownLabel>Whole project overview</DropdownLabel>
          </DropdownItem>
          {engineId !== null && (
            <DropdownItem onClick={() => onFocus(engineId)}>
              <CogIcon />
              <DropdownLabel>Focus on the working engine</DropdownLabel>
            </DropdownItem>
          )}
          <DropdownItem onClick={onFind}>
            <SearchIcon />
            <DropdownLabel>Find a part, document or reference</DropdownLabel>
          </DropdownItem>
          <DropdownItem onClick={() => void copyLink()}>
            <LinkIcon />
            <DropdownLabel>Copy link to this view</DropdownLabel>
          </DropdownItem>
        </DropdownSection>
        <DropdownDivider />
        <DropdownSection>
          <DropdownHeading>Status tables</DropdownHeading>
          {STATUS_TABLES.map(({ tab, label }) => (
            <DropdownItem key={tab} href={`/projects/${encodeURIComponent(projectId)}?tab=${tab}`}>
              <DropdownLabel>{label}</DropdownLabel>
            </DropdownItem>
          ))}
        </DropdownSection>
      </DropdownMenu>
    </Dropdown>
  );
}
